import areExternalScriptsDisabled from './areExternalScriptsDisabled';
import listContextCacheMaxEntries from '../configs/listContextCacheMaxEntries';

const STORAGE_KEY = 'pdp-list-context-map';

// Structural shape of a stored/returned list origin. Declared locally (rather than importing
// `ProductActionsOriginType` from `trackProductActions`) so this module stays a leaf and does not
// form an import cycle with the tracking event pipeline (`pushToDataLayer` imports from here).
// It is structurally compatible with `ProductActionsOriginType`.
export interface ListContext {
  readonly originId: string;
  readonly originType: string;
}

interface StoredListContextEntry extends ListContext {
  readonly productCode: string;
}

function isStoredListContextEntry(value: unknown): value is StoredListContextEntry {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as StoredListContextEntry).productCode === 'string' &&
    typeof (value as StoredListContextEntry).originId === 'string' &&
    typeof (value as StoredListContextEntry).originType === 'string'
  );
}

function readEntries(): Array<StoredListContextEntry> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isStoredListContextEntry);
  } catch {
    return [];
  }
}

function writeEntries(entries: ReadonlyArray<StoredListContextEntry>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    // Drop any legacy per-tab entry so pre-migration sessionStorage data can't linger.
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable or quota exceeded
  }
}

export function setListContext(productCode: string, context: ListContext): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (areExternalScriptsDisabled()) {
    // Opted out: never populate the store, and drop anything already there.
    clearListContext();
    return;
  }
  const withoutExisting = readEntries().filter((entry) => entry.productCode !== productCode);
  const updated = [...withoutExisting, { productCode, ...context }];
  while (updated.length > listContextCacheMaxEntries) {
    updated.shift();
  }
  writeEntries(updated);
}

export function clearListContext(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
    // Also drop any legacy per-tab entry.
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable
  }
}

export function getListContext(
  productCode: string,
  siblingCodes?: ReadonlyArray<string>
): ListContext | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  if (areExternalScriptsDisabled()) {
    return undefined;
  }
  const entries = readEntries();
  const match =
    entries.find((entry) => entry.productCode === productCode) ??
    entries.find((entry) => siblingCodes?.includes(entry.productCode) === true);
  return match ? { originId: match.originId, originType: match.originType } : undefined;
}
